import { useEffect, useState } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Spinner, Empty } from "@/components/common";
import { LifeBuoy, BookOpen, Plus, X, LogOut, Ticket as TicketIcon, Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/ui";

const PRIO = { P1: "text-rose-300 bg-rose-950/50", P2: "text-amber-300 bg-amber-950/50", P3: "text-sky-300 bg-sky-950/50", P4: "text-slate-300 bg-slate-800" };
const STATUS_TONE = { New: "text-cyan-300", Open: "text-amber-300", Resolved: "text-emerald-300", Closed: "text-slate-400" };

export default function Portal() {
  const { user, logout, can } = useAuth();
  const [brand, setBrand] = useState(null);
  const [tab, setTab] = useState("tickets");

  useEffect(() => { api.get("/portal/branding").then((r) => setBrand(r.data)).catch(() => setBrand({ name: "IT Support Portal", has_logo: false })); }, []);
  if (!brand) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#0B0E14" }}><Spinner /></div>;

  return (
    <div className="min-h-screen" style={{ background: "#0B0E14" }} data-testid="portal">
      <header className="border-b border-slate-800 bg-[#090C10]">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center gap-3">
          {brand.has_logo && brand.customer_id
            ? <img src={`${API}/customers/${brand.customer_id}/logo`} alt={brand.name} className="h-9 max-w-[160px] object-contain" onError={(e) => { e.target.style.display = "none"; }} />
            : <div className="w-9 h-9 rounded-md bg-cyan-500/10 flex items-center justify-center"><LifeBuoy size={20} className="text-cyan-400" /></div>}
          <div className="leading-none">
            <div className="font-heading font-extrabold text-[16px] text-slate-100">{brand.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">IT Support Portal</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-[12px] text-slate-400">{user?.name}</span>
            <button data-testid="portal-logout" onClick={logout} className="text-slate-500 hover:text-rose-400 p-1"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-6">
        {brand.welcome && <div className="mb-5 rounded-lg bg-cyan-500/10 border border-cyan-800/40 px-4 py-3 text-[14px] text-cyan-100">{brand.welcome}</div>}
        <div className="flex gap-1 border-b border-slate-800 mb-5">
          <TabBtn active={tab === "tickets"} onClick={() => setTab("tickets")} icon={TicketIcon} label="My Tickets" testid="portal-tab-tickets" />
          <TabBtn active={tab === "kb"} onClick={() => setTab("kb")} icon={BookOpen} label="Knowledge Base" testid="portal-tab-kb" />
        </div>
        {tab === "tickets" ? <PortalTickets brand={brand} canCreate={can("tickets:create")} /> : <PortalKB />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return <button data-testid={testid} onClick={onClick} className={"flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors " + (active ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200")}><Icon size={15} /> {label}</button>;
}

function PortalTickets({ brand, canCreate }) {
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState(null);
  const [f, setF] = useState({ subject: "", description: "", priority: "P3", ticket_type: "Service Request" });

  const load = () => api.get("/tickets").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/tickets", { ...f, customer_id: brand.customer_id }); toast.success("Ticket submitted"); setShow(false); setF({ subject: "", description: "", priority: "P3", ticket_type: "Service Request" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const open = async (id) => { try { const { data } = await api.get(`/tickets/${id}`); setDetail(data); } catch (e) { toast.error(apiErr(e)); } };

  if (!rows) return <Spinner />;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-bold text-slate-200 text-lg">Support Tickets</h2>
        {canCreate && <Btn data-testid="portal-new-ticket" onClick={() => setShow(true)}><Plus size={15} /> New Request</Btn>}
      </div>
      {rows.length === 0 ? <Empty icon={TicketIcon} title="No tickets yet" hint={canCreate ? "Open a request and our team will get right on it." : "You have no support tickets."} /> : (
        <div className="space-y-2">
          {rows.map((t) => (
            <div key={t.id} data-testid="portal-ticket-row" onClick={() => open(t.id)} className="card-surface rounded-lg px-4 py-3 flex items-center gap-3 cursor-pointer hover:border-cyan-800 transition-colors">
              <span className={"text-[10px] font-bold uppercase rounded px-1.5 py-0.5 " + (PRIO[t.priority] || PRIO.P4)}>{t.priority}</span>
              <div className="flex-1 min-w-0"><div className="text-[14px] text-slate-100 truncate">#{t.number} · {t.subject}</div><div className="text-[11px] text-slate-500">{t.ticket_type} · updated {timeAgo(t.updated_at)}</div></div>
              <span className={"text-[12px] font-semibold " + (STATUS_TONE[t.status] || "text-slate-400")}>{t.status}</span>
              <ChevronRight size={16} className="text-slate-600" />
            </div>
          ))}
        </div>
      )}

      {show && (
        <Modal title="New Support Request" onClose={() => setShow(false)}>
          <label className="lbl">Subject</label><input data-testid="portal-ticket-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="inp" placeholder="Brief summary of the issue" />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="lbl">Type</label><select data-testid="portal-ticket-type" value={f.ticket_type} onChange={(e) => setF({ ...f, ticket_type: e.target.value })} className="inp"><option>Service Request</option><option>Incident</option><option>Change / Maintenance</option><option>Procurement</option></select></div>
            <div><label className="lbl">Priority</label><select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="inp"><option value="P4">Low</option><option value="P3">Normal</option><option value="P2">High</option><option value="P1">Urgent</option></select></div>
          </div>
          <label className="lbl">Details</label><textarea data-testid="portal-ticket-body" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={5} className="inp" placeholder="Describe what's happening…" />
          <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancel</Btn><Btn data-testid="portal-ticket-submit" onClick={create} disabled={!f.subject || !f.description}>Submit</Btn></div>
        </Modal>
      )}
      {detail && (
        <Modal title={`#${detail.number} · ${detail.subject}`} onClose={() => setDetail(null)}>
          <div className="flex items-center gap-2 mb-3"><span className={"text-[10px] font-bold uppercase rounded px-1.5 py-0.5 " + (PRIO[detail.priority] || PRIO.P4)}>{detail.priority}</span><span className={"text-[12px] font-semibold " + (STATUS_TONE[detail.status] || "text-slate-400")}>{detail.status}</span><span className="text-[11px] text-slate-500 ml-auto">opened {timeAgo(detail.created_at)}</span></div>
          <div className="text-[13px] text-slate-300 whitespace-pre-wrap bg-slate-900/50 border border-slate-800 rounded p-3">{detail.description || "—"}</div>
        </Modal>
      )}
      <style>{PORTAL_CSS}</style>
    </div>
  );
}

function PortalKB() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);
  const load = () => api.get("/kb/articles", { params: q ? { q } : {} }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  const open = async (id) => { try { const { data } = await api.get(`/kb/articles/${id}`); setView(data); } catch (e) { toast.error(apiErr(e)); } };
  if (!rows) return <Spinner />;
  return (
    <div>
      <div className="relative mb-4 max-w-md"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" /><input data-testid="portal-kb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help articles…" className="w-full bg-slate-900 border border-slate-800 rounded-md pl-9 pr-3 py-2 text-[13px] text-slate-200 outline-none" /></div>
      {rows.length === 0 ? <Empty icon={BookOpen} title="No articles found" hint="No published help articles match your search." /> : (
        <div className="grid md:grid-cols-2 gap-3">
          {rows.map((a) => (
            <div key={a.id} data-testid="portal-kb-card" onClick={() => open(a.id)} className="card-surface rounded-lg p-4 cursor-pointer hover:border-cyan-800 transition-colors">
              <span className="text-[10px] uppercase tracking-wider text-cyan-300 bg-cyan-950/40 rounded px-1.5 py-0.5">{a.category}</span>
              <h3 className="font-heading font-bold text-slate-100 text-[15px] mt-2">{a.title}</h3>
              <p className="text-[12px] text-slate-500 line-clamp-2 mt-1">{(a.body || "").replace(/[#*`>_-]/g, "").slice(0, 140)}</p>
            </div>
          ))}
        </div>
      )}
      {view && (
        <Modal title={view.title} onClose={() => setView(null)}>
          <div className="text-[11px] text-slate-500 mb-3">{view.category} · updated {timeAgo(view.updated_at)}</div>
          <div className="text-[14px] text-slate-300 whitespace-pre-wrap leading-relaxed" data-testid="portal-kb-body">{view.body}</div>
        </Modal>
      )}
      <style>{PORTAL_CSS}</style>
    </div>
  );
}

const PORTAL_CSS = `.inp{width:100%;background:#0b1220;border:1px solid #334155;border-radius:6px;padding:8px 12px;font-size:13px;color:#e2e8f0;outline:none;margin-bottom:12px}.lbl{display:block;font-size:12px;color:#94a3b8;margin-bottom:4px}.line-clamp-2{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}`;

function Modal({ title, children, onClose }) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}><div className="card-surface rounded-xl p-5 w-full max-w-lg max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}><div className="flex items-center justify-between mb-4"><h2 className="font-heading font-bold text-slate-100 pr-4">{title}</h2><button onClick={onClose} className="text-slate-500 hover:text-slate-200 shrink-0"><X size={18} /></button></div>{children}</div></div>;
}
